import { h, Component } from 'preact';
import { Text, Localizer } from 'preact-i18n';

import {
    filterDiagnoseResults,
    isDiagnoseOnJournal,
    toggleDiagnoseSelection,
    removeDiagnoseSelection,
    addDiagnosesSequentially,
} from '../../lib/diagnoseMultiPicker';

const SEARCH_DEBOUNCE_MS = 300;

function diagnoseKey(diagnose) {
    return diagnose.diagnoseCode || String(diagnose.id);
}

/**
 * «Velg flere» mode of the «Legg til diagnose» drawer (addDiagnose.js).
 * TICKET-01712: tick several diagnoses, also across searches, and add them in
 * one go, like the sub-product picker (components/product/addSubProducts.js).
 *
 * The drawer owns the saving: `saveOne(diagnose)` stores one diagnose on the
 * journal (throws on failure) and `onDone(addedCount)` reloads the journal and
 * resets the drawer once after a batch. `children` (the drawer's toggles) are
 * rendered right under the search field.
 */
class DiagnoseMultiPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            foundList: [],
            selected: [],
            isSearching: false,
            isAdding: false,
            addedCount: 0,
            addTotal: 0,
            addError: false,
        };
        this.searchTimer = null;
        this.searchSeq = 0;
        this.searchInputField = null;
    }

    componentDidMount() {
        const { setFocusCallback } = this.props;
        if (setFocusCallback) {
            setFocusCallback(() => this.searchInputField?.focus());
        }
        this.searchInputField?.focus();
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
    }

    runSearch = async (search) => {
        const { diagnoseStore } = this.props;
        this.searchSeq += 1;
        const seq = this.searchSeq;
        if (!search.trim()) {
            this.setState({ foundList: [], isSearching: false });
            return;
        }
        this.setState({ isSearching: true });
        let results = [];
        try {
            results = await diagnoseStore.load({ query: { search }, skipUpdate: true });
        } catch (err) {
            results = [];
        }
        if (seq !== this.searchSeq) {
            return; // A newer search superseded this one.
        }
        this.setState({ foundList: filterDiagnoseResults(results, search), isSearching: false });
    }

    onSearchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.runSearch(search), SEARCH_DEBOUNCE_MS);
    }

    onToggleDiagnose = (e) => {
        const { idx } = e.currentTarget.dataset;
        const { journalDiagnoses } = this.props;
        const { foundList, selected, isAdding } = this.state;
        const diagnose = foundList[parseInt(idx, 10)];
        if (!diagnose || isAdding || isDiagnoseOnJournal(journalDiagnoses, diagnose)) {
            return;
        }
        this.setState({ selected: toggleDiagnoseSelection(selected, diagnose), addError: false });
    }

    onRemoveSelected = (e) => {
        const { idx } = e.currentTarget.dataset;
        const { selected } = this.state;
        const diagnose = selected[parseInt(idx, 10)];
        if (!diagnose) {
            return;
        }
        this.setState({ selected: removeDiagnoseSelection(selected, diagnose) });
    }

    onClickAdd = async () => {
        const { saveOne, onDone = () => {} } = this.props;
        const { selected, isAdding } = this.state;
        if (isAdding || selected.length === 0) {
            return;
        }
        this.setState({
            isAdding: true, addedCount: 0, addTotal: selected.length, addError: false,
        });
        const { added, remaining, error } = await addDiagnosesSequentially(
            selected,
            saveOne,
            done => this.setState({ addedCount: done }),
        );
        let failed = !!error;
        if (error) {
            console.error('Failed to add diagnoses', error);
        }
        if (added > 0) {
            try {
                await onDone(added);
            } catch (err) {
                console.error('Failed to reload journal after adding diagnoses', err);
                failed = true;
            }
        }
        this.setState({ isAdding: false, selected: remaining, addError: failed });
    }

    render() {
        const { journalDiagnoses, children } = this.props;
        const {
            search, foundList, selected, isSearching, isAdding, addedCount, addTotal, addError,
        } = this.state;
        const canAdd = selected.length > 0 && !isAdding;
        const showNoResults = !isSearching && !!search.trim() && foundList.length === 0;

        return (
            <div class='w-100 d-flex flex-column'>
                <div class='text-body-secondary small mb-1'>
                    <Text id='dyrejournal.diagnose-multi-hint'>Tick the diagnoses you want to add, then press Add.</Text>
                </div>
                <div class='position-relative'>
                    <Localizer>
                        <input
                            class='form-control diagnose-multi-search'
                            type='text'
                            placeholder={<Text id='dyrejournal.diagnose-search-title'>Search for diagnose</Text>}
                            onInput={this.onSearchInput}
                            value={search}
                            disabled={isAdding}
                            ref={c => { this.searchInputField = c; }}
                        />
                    </Localizer>
                    {isSearching && (
                        <div class='position-absolute' style='right: 10px; top: 50%; transform: translateY(-50%);'>
                            <div class='spinner-border spinner-border-sm text-primary' role='status' />
                        </div>
                    )}
                </div>

                {children}

                {selected.length > 0 && (
                    <div class='mb-3'>
                        <div class='text-body-secondary small text-uppercase mb-1'>
                            <Text id='dyrejournal.diagnose-multi-selected-title'>Selected diagnoses</Text> ({selected.length})
                        </div>
                        <div class='list-group'>
                            {selected.map((diagnose, idx) => (
                                <div key={diagnoseKey(diagnose)} class='list-group-item d-flex flex-row align-items-center gap-2 py-1 diagnose-multi-selected'>
                                    <div class='flex-fill'>
                                        {diagnose.diagnoseCode}: {diagnose.name}
                                    </div>
                                    <Localizer>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-danger rounded-pill diagnose-multi-remove'
                                            data-idx={idx}
                                            data-code={diagnoseKey(diagnose)}
                                            onClick={this.onRemoveSelected}
                                            disabled={isAdding}
                                            title={<Text id='dyrejournal.diagnose-multi-remove'>Remove</Text>}
                                        >
                                            <i class='fa-solid fa-xmark' />
                                        </button>
                                    </Localizer>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {foundList.length > 0 && (
                    <div class='mb-3'>
                        <div class='list-group' style='max-height: 40vh; overflow-y: auto;'>
                            {foundList.map((diagnose, idx) => {
                                const onJournal = isDiagnoseOnJournal(journalDiagnoses, diagnose);
                                const checked = selected.some(d => diagnoseKey(d) === diagnoseKey(diagnose));
                                return (
                                    <label
                                        key={diagnoseKey(diagnose)}
                                        class={`list-group-item d-flex flex-row align-items-center gap-2 py-1 ${onJournal ? 'text-body-tertiary' : ''}`}
                                        style={onJournal ? 'cursor: not-allowed;' : 'cursor: pointer;'}
                                    >
                                        <input
                                            class='form-check-input m-0 flex-shrink-0 diagnose-multi-check'
                                            type='checkbox'
                                            checked={checked}
                                            disabled={onJournal || isAdding}
                                            data-idx={idx}
                                            data-code={diagnoseKey(diagnose)}
                                            onChange={this.onToggleDiagnose}
                                        />
                                        <div class='flex-fill'>
                                            {diagnose.diagnoseCode}: {diagnose.name}
                                        </div>
                                        {onJournal && (
                                            <span class='badge bg-body-secondary text-body-secondary'>
                                                <Text id='dyrejournal.diagnose-multi-already-added'>Already added</Text>
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
                {showNoResults && (
                    <div class='mb-3 text-body-secondary'>
                        <Text id='dyrejournal.diagnose-multi-no-results'>No diagnoses found</Text>
                    </div>
                )}

                {addError && (
                    <div class='alert alert-danger py-2 diagnose-multi-error'>
                        <Text id='dyrejournal.diagnose-multi-add-error'>Some diagnoses were not added. Try again.</Text>
                    </div>
                )}
                <button type='button' class='btn btn-primary rounded-pill mb-2 diagnose-multi-add' onClick={this.onClickAdd} disabled={!canAdd}>
                    {isAdding ? (
                        <span>
                            <i class='fa-solid fa-spinner fa-spin me-2' />
                            <Text id='dyrejournal.diagnose-multi-adding-progress' fields={{ done: addedCount, total: addTotal }}>
                                Adding {addedCount} of {addTotal}...
                            </Text>
                        </span>
                    ) : (
                        <Text id='dyrejournal.diagnose-multi-add-count' fields={{ count: selected.length }}>
                            Add {selected.length}
                        </Text>
                    )}
                </button>
            </div>
        );
    }
}

export default DiagnoseMultiPicker;
